import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, IconButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import styled from '@emotion/styled';
import { CustomContentProps, SnackbarContent, closeSnackbar } from 'notistack';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

const variantIcons = {
  success: {
    icon: <CheckCircleIcon />,
  },
  error: {
    icon: <ErrorIcon />,
  },
  warning: {
    icon: <WarningIcon />,
  },
  info: {
    icon: <InfoIcon />,
  },
  default: {
    icon: <InfoIcon />,
  },
};

const variantLabelKeys = {
  success: 'snackbar.success',
  error: 'snackbar.error',
  warning: 'snackbar.warning',
  info: 'snackbar.info',
  default: 'snackbar.default',
} as const;

const Content = styled(SnackbarContent)`
  display: flex;
  align-items: stretch;
  width: min(420px, calc(100vw - 32px));

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: calc(100vw - 24px);
  }
`;

const Surface = styled(Box)<{ accent: string }>(({ theme, accent }) => ({
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  width: '100%',
  padding: theme.spacing(1.5, 1.5, 1.5, 1.75),
  border: `1px solid ${alpha(accent, 0.28)}`,
  borderLeft: `5px solid ${accent}`,
  borderRadius: 18,
  color: theme.palette.text.primary,
  background: theme.customTokens.surfaces.snackbarSurface(accent),
  boxShadow:
    theme.palette.mode === 'dark'
      ? `0 18px 44px ${alpha(theme.palette.common.black, 0.44)}`
      : `0 18px 44px ${alpha(theme.palette.text.primary, 0.16)}`,
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderRadius: 16,
  },
}));

const IconWrapper = styled(Box)<{ accent: string }>(({ theme, accent }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  flex: '0 0 auto',
  borderRadius: 14,
  color: accent,
  backgroundColor: alpha(accent, theme.palette.mode === 'dark' ? 0.18 : 0.1),
}));

const AppSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ id, message, variant }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const variantKey =
      variant && Object.prototype.hasOwnProperty.call(variantIcons, variant)
        ? (variant as keyof typeof variantIcons)
        : 'default';
    const icon = variantIcons[variantKey]?.icon || variantIcons.default.icon;
    const labelKey = variantLabelKeys[variantKey] || variantLabelKeys.default;
    const label = t(labelKey);
    const accentByVariant = {
      success: theme.palette.success.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main,
      default: theme.palette.primary.main,
    };
    const accent =
      accentByVariant[(variant as keyof typeof accentByVariant) || 'default'] ||
      accentByVariant.default;

    return (
      <Content ref={ref} role="alert">
        <Surface accent={accent}>
          <IconWrapper accent={accent}>{icon}</IconWrapper>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="subtitle2">{label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {message}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={t('snackbar.close')}
            onClick={() => closeSnackbar(id)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Surface>
      </Content>
    );
  }
);

AppSnackbar.displayName = 'AppSnackbar';

export default AppSnackbar;
